import React from 'react';
import { Box, ButtonV2, Flex, TextV2 } from '@withjoy/joykit';
import { CheckOutline, Close } from '@withjoy/joykit/icons';
import { pxToRem } from '@withjoy/joykit/theme';
import { useTranslation } from '@shared/core';

type AutoPopulatedStayAreasContentProps = Readonly<{
  onClose: () => void;
  map?: React.ReactNode;
  showCta?: boolean;
  loading?: boolean;
  onCtaClick?: () => void;
  children?: never;
}>;

export const AutoPopulatedStayAreasContent: React.FC<AutoPopulatedStayAreasContentProps> = ({ onClose, map, showCta, onCtaClick, loading }) => {
  const { tq } = useTranslation('stayAreasAdmin');

  const benefits = [
    tq`Some guests prefer to book vacation rentals to stay together as a group, have more privacy or flexibility.`,
    tq`Vacation rentals availability changes very fast, so suggesting neighborhoods and letting your guests browse stays there is the most helpful.`,
    tq`We recommend suggesting 3-5 neighborhoods to stay near your venue.`,
  ];

  return (
    <Box
      padding={7}
      display="flex"
      flexDirection="column"
      gap={6}
      position="relative"
    >
      {/* Close button */}
      <ButtonV2
        variant="ghost"
        intent="neutral"
        onClick={onClose}
        aria-label={tq`Close Neighborhoods Tooltip`}
        padding={0}
        width={pxToRem(32)}
        minWidth={pxToRem(32)}
        height={pxToRem(32)}
        position="absolute"
        top={2}
        right={2}
        zIndex={1}
      >
        <Close size={16} />
      </ButtonV2>

      {/* Map */}
      {map}

      {/* Content */}
      <Flex
        flexDirection="column"
        gap={4}
      >
        {/* Heading */}
        <TextV2
          typographyVariant="hed4"
          color="mono14"
        >{tq`Why add Neighborhoods?`}</TextV2>

        {/* Benefits List */}
        <Flex
          flexDirection="column"
          gap={3}
        >
          {benefits.map((text, index) => (
            <Flex
              key={index}
              gap={2}
              alignItems="flex-start"
            >
              <Box flexShrink={0}>
                <CheckOutline
                  size={24}
                  color="mono14"
                />
              </Box>
              <TextV2
                typographyVariant="body1"
                color="mono14"
              >
                {text}
              </TextV2>
            </Flex>
          ))}
        </Flex>
      </Flex>

      {showCta && (
        <ButtonV2
          width="100%"
          shape="rounded"
          intent="primary"
          onClick={onCtaClick}
          loading={loading}
        >
          {tq`Turn On Neighborhoods`}
        </ButtonV2>
      )}
    </Box>
  );
};
